import { useEffect, useState } from 'react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'
import authService, {
  Status as AuthStatus,
} from 'modules/payroll_integrations_datev_api/lib/authService'
import Relink from 'modules/payroll_integrations_kombo/components/SupplementsSyncPreview/Relink'
import CompanyInstallationsChecker from 'modules/payroll_integrations_kombo/lib/CompanyInstallations/checker'
import { IntegrationLabels } from 'modules/payroll_integrations_kombo/lib/constants'

import SupplementsToSync from './SupplementsToSync'

interface Props {
  policyPeriodId: number
  disableSubmitCallback: (value: boolean) => void
}

const SupplementsSyncPreview = ({ policyPeriodId, disableSubmitCallback }: Props) => {
  const [datevApiInstalled, setDatevApiInstalled] = useState<boolean>(false)
  const [datevLugInstalled, setDatevLugInstalled] = useState<boolean>(false)

  const [integrationLabel, setIntegrationLabel] = useState<string>()
  const [blockedSubmit, setBlockedSubmit] = useState<boolean>(true)
  const [authStatus, setAuthStatus] = useState<AuthStatus>(AuthStatus.NotAvailable)
  const isBookkeeperView = useBookkeeperView()
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const fetchAuthStatus = async () => {
    const currentAuthStatus = await authService.getStatus({
      companyId: companyId,
      bookkeeperView: isBookkeeperView,
    })
    if (currentAuthStatus != AuthStatus.AuthenticationExpired) {
      setBlockedSubmit(false)
      disableSubmitCallback(false)
    }
    setAuthStatus(currentAuthStatus)
  }

  const connect = async () => {
    await authService.connect(authStatus, companyId)
    fetchAuthStatus()
  }

  const { loading } = useAsyncData(async () => {
    setDatevApiInstalled(await CompanyInstallationsChecker.isDatevApiInstalled())
    setDatevLugInstalled(await CompanyInstallationsChecker.isDatevLugInstalled())
  }, [policyPeriodId])

  useEffect(() => {
    if (!datevApiInstalled && !datevLugInstalled) return

    disableSubmitCallback(true)
    if (datevApiInstalled) {
      setIntegrationLabel(IntegrationLabels.datev)
    } else if (datevLugInstalled) {
      setIntegrationLabel(IntegrationLabels.datevLug)
    }
    fetchAuthStatus()
  }, [datevApiInstalled, datevLugInstalled]) // eslint-disable-line react-hooks/exhaustive-deps

  const show =
    !loading &&
    (datevApiInstalled || datevLugInstalled) &&
    integrationLabel &&
    authStatus !== AuthStatus.NotAvailable

  if (!show) return <></>

  return (
    <>
      {authStatus == AuthStatus.Authenticated ? (
        <SupplementsToSync
          policyPeriodId={policyPeriodId}
          integrationLabel={integrationLabel}
        ></SupplementsToSync>
      ) : (
        <Relink
          onClick={connect}
          blockedSubmit={blockedSubmit}
          integrationLabel={integrationLabel}
        />
      )}
    </>
  )
}

export default SupplementsSyncPreview
